import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { getProductById, isProductSeoEnabled } from '../data/products'
import SeoHead, { generateProductSchema, generateBreadcrumbSchema } from '../components/SeoHead'
import './ProductDetails.css'

const ProductDetails = () => {
  const { productId } = useParams()
  const product = getProductById(productId)
  const [activeTab, setActiveTab] = useState('ingredients')
  const [selectedVariant, setSelectedVariant] = useState(0)

  if (!product) {
    return (
      <div className="product-not-found">
        <SeoHead
          title="Product Not Found | SIBR Lifescience"
          description="The product you are looking for could not be found."
          canonical={`/products/${productId}`}
          noindex={true}
        />
        <div className="container">
          <h1>Product Not Found</h1>
          <p>The product you're looking for doesn't exist or has been removed.</p>
          <Link to="/products" className="btn btn-primary">
            View All Products
          </Link>
        </div>
      </div>
    )
  }

  // Check if SEO is enabled for this product
  const seoEnabled = isProductSeoEnabled(product)

  // Generate schemas
  const productSchema = generateProductSchema(product)
  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', url: '/' },
    { name: 'Products', url: '/products' },
    { name: product.name }
  ])
  
  // Combined schema
  const combinedSchema = [productSchema, breadcrumbSchema]
  
  // Extract key benefit for SEO title (first benefit, truncated)
  const keyBenefit = product.benefits[0]?.split(':')[0] || product.tagline.split(' ').slice(0, 4).join(' ')

  const tabs = [
    { id: 'ingredients', label: 'Ingredients' },
    { id: 'mechanism', label: 'Mechanism of Action' },
    { id: 'indications', label: 'Indications' },
    { id: 'safety', label: 'Safety & Usage' }
  ]

  return (
    <div className="product-details-page">
      <SeoHead
        title={`${product.name} | ${keyBenefit} | SIBR Lifescience`}
        description={product.shortDescription}
        canonical={`/products/${product.id}`}
        type="product"
        image={product.image}
        schema={combinedSchema}
        seoEnabled={seoEnabled}
      />
      
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Home</Link>
          <span>/</span>
          <Link to="/products">Products</Link>
          <span>/</span>
          <span className="current">{product.name}</span>
        </div>
      </div>

      {/* Product Header */}
      <section className="product-header">
        <div className="container">
          <div className="product-header-grid">
            <div className="product-image-section">
              <div className="product-main-image">
                {product.variants && product.variants.length > 0 ? (
                  <img 
                    src={product.variants[selectedVariant].image} 
                    alt={`${product.name} ${product.variants[selectedVariant].name} - SIBR Lifescience pharmaceutical medicine`}
                    className="product-detail-img"
                  />
                ) : product.image ? (
                  <img 
                    src={product.image} 
                    alt={`${product.name} - SIBR Lifescience pharmaceutical medicine`}
                    className="product-detail-img"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="product-placeholder-large" style={{ display: (product.image || product.variants) ? 'none' : 'flex' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
                    <path d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z"/>
                  </svg>
                </div>
              </div>
              {/* Variant Thumbnails */}
              {product.variants && product.variants.length > 0 && (
                <div className="variant-thumbnails">
                  {product.variants.map((variant, index) => (
                    <button
                      key={index}
                      className={`variant-thumb ${selectedVariant === index ? 'active' : ''}`}
                      onClick={() => setSelectedVariant(index)}
                      title={variant.name}
                    >
                      <img src={variant.image} alt={variant.name} />
                      <span className="variant-name">{variant.name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="product-info-section">
              <h1>{product.name}</h1>
              <p className="product-tagline-large">{product.tagline}</p>

              {/* Quick Facts */}
              <div className="quick-facts">
                <h2 className="section-heading">Key Benefits</h2>
                <ul className="benefits-list">
                  {product.benefits.map((benefit, index) => (
                    <li key={index}>
                      <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                      </svg>
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>

              {/* CTA */}
              <div className="product-cta">
                {product.link1mg && (
                  <a 
                    href={product.link1mg}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary btn-lg"
                  >
                    Find on 1mg
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                      <polyline points="15 3 21 3 21 9"/>
                      <line x1="10" y1="14" x2="21" y2="3"/>
                    </svg>
                  </a>
                )}
                <Link to="/contact" className={`btn ${product.link1mg ? 'btn-secondary' : 'btn-primary'} btn-lg`}>
                  Contact for Enquiry
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tabbed Content */}
      <section className="product-tabs-section">
        <div className="container">
          <div className="tabs-navigation">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="tab-content">
            {activeTab === 'ingredients' && (
              <div className="tab-panel">
                <h2 className="tab-heading">Ingredients</h2>
                {product.ingredients && product.ingredients.length > 0 ? (
                  <div className="ingredients-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Ingredient</th>
                          <th>Category</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {product.ingredients.map((ing, index) => (
                          <tr key={index}>
                            <td><strong>{ing.name}</strong></td>
                            <td>{ing.category}</td>
                            <td>{ing.amount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p>Please consult the product packaging for detailed ingredient information.</p>
                )}
              </div>
            )}

            {activeTab === 'mechanism' && (
              <div className="tab-panel">
                <h2 className="tab-heading">Mechanism of Action</h2>
                <div className="mechanism-content">
                  <p>{product.mechanism}</p>
                </div>
              </div>
            )}

            {activeTab === 'indications' && (
              <div className="tab-panel">
                <h2 className="tab-heading">Indications</h2>
                <ul className="indications-list">
                  {product.indications.map((indication, index) => (
                    <li key={index}>{indication}</li>
                  ))}
                </ul>
                <p className="dosage-info">
                  <strong>Dosage:</strong> {product.dosage}
                </p>
              </div>
            )}

            {activeTab === 'safety' && (
              <div className="tab-panel">
                <h2 className="tab-heading">Safety & Usage Guidelines</h2>
                <ul className="safety-list">
                  {product.safety.map((item, index) => (
                    <li key={index}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="18" height="18">
                        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mandatory Disclaimer */}
      <section className="product-disclaimer">
        <div className="container">
          <div className="disclaimer-box">
            <div className="disclaimer-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"/>
              </svg>
            </div>
            <div className="disclaimer-text">
              <h4>Medical Disclaimer</h4>
              <p>
                The information provided on this page is for <strong>INFORMATIONAL PURPOSES ONLY</strong> and 
                is not intended as a substitute for professional medical advice, diagnosis, or treatment. 
                Always seek the advice of your physician or other qualified healthcare provider with any 
                questions you may have regarding a medical condition or medication.
              </p>
              <Link to="/disclaimer">Read Full Disclaimer →</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products CTA */}
      <section className="related-cta section-sm bg-light">
        <div className="container">
          <div className="related-cta-content">
            <h3>Explore More Products</h3>
            <p>Discover our complete range of pharmaceutical products.</p>
            <div className="related-cta-actions">
              <Link to="/products" className="btn btn-primary">
                View All Products
              </Link>
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
              >
                Shop on 1mg
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ProductDetails

